<script setup lang="ts">
import { computed } from 'vue'
import { useMapStore } from '@/stores/mapStore.ts'
import { useMapSettingsStore } from '@/stores/mapSettingsStore.ts'
import { useMapCoordinateStore } from '@/stores/mapCoordinateStore.ts'
import { usePlayerRelationStore } from '@/stores/playerRelations.ts'
import { zoomFactor } from '@/enums/zoomFactor.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import FilterButton from '@/components/Elements/Buttons/FilterButton.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { useGameStore } from '@/stores/gameStore.ts'
/**
 * Stores / models
 */
const mapStore = useMapStore()
const mapSettingsStore = useMapSettingsStore()
const mapCoordinateStore = useMapCoordinateStore()
const playerRelationsStore = usePlayerRelationStore()
const gameStore = useGameStore()

const largeFleets = computed(() => {
  return Array.from(mapCoordinateStore.fleets.values())
    .filter(fleet => fleet.playerId !== gameStore.player?.id)
    .filter(fleet => fleet.ships > 50)
})

/**
 * variables
 */
const presets = [
  { name: 'Planet', value: zoomFactor.Planet },
  { name: { mobile: 'Sol', desktop: 'Solar' }, value: zoomFactor.Solar },
  { name: 'Local', value: zoomFactor.Local },
  { name: 'Sector', value: zoomFactor.Sector },
  { name: { mobile: 'Quad', desktop: 'Quadrant' }, value: zoomFactor.Quadrant },
  { name: 'Max', value: zoomFactor.Max }
]

/**
 * Functions
 */
const zoom = (zoom: number) => {
  mapStore.zoomFactor = zoom
}

const openSettings = () => {
  gameStore.modalType = 'mapSettings'
}

const toggleLargeFleets = () => {
  mapSettingsStore.showLargeFleets = !mapSettingsStore.showLargeFleets
}
</script>

<template>
  <div class="pt-2 pointer-events-auto flex justify-center xl:justify-end">
    <div class="inline-flex">
      <div 
        v-for="preset in presets" 
        :key="preset.value"
        :class="preset.name === 'Max' ? 'hidden md:block' : ''"
      >
        <FilterButton
          class="mr-2"
          :active="mapStore.zoomFactor === preset.value"
          @click="zoom(preset.value)"
        >
          <template v-if="typeof preset.name === 'string'">
            {{ preset.name }}
          </template>
          <template v-else>
            <span class="md:hidden">{{ preset.name.mobile }}</span>
            <span class="hidden md:inline">{{ preset.name.desktop }}</span>
          </template>
        </FilterButton>
      </div>
      <ActionButton
        :for-icon="true"
        class="mr-2 xl:hidden relative"
        @click="toggleLargeFleets"
      >
        <GenericIcon
          type="ship"
          tooltip="Toggle Large Fleets"
          :class="{ 'opacity-50': !mapSettingsStore.showLargeFleets }"
        />
        <span
          v-if="!mapSettingsStore.showLargeFleets && largeFleets.length > 0"
          class="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold"
        >
          {{ largeFleets.length }}
        </span>
      </ActionButton>
      <ActionButton :for-icon="true" class="mr-2" @click="openSettings">
        <GenericIcon type="cog" tooltip="Map Settings" />
      </ActionButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* Add any additional styles if needed */
</style>
